import { CustomBottomBar, FileUploader, MinimalStepper } from '@components';
import { Button, Checkbox, Container, Dropdown, Icon, Input, InputDropdownMulti, Textarea, Typography } from '@design-system';
import React from 'react';
import './NewQuitusPage.scss';
import { useNewQuitusPageVM } from './useNewQuitusPageVM';
import { LiaArrowLeftSolid } from 'react-icons/lia';
import { MdKeyboardArrowRight } from 'react-icons/md';
import SignatureCanvas from 'react-signature-canvas';
import { RiArrowGoBackLine } from 'react-icons/ri';

export const NewQuitusPage: React.FC = () => {
    const {
        currentStep,
        steps,
        operatorsOptions,
        worksitesOptions,
        apartmentsOptions,
        quitusTypesOptions,
        buildingsOptions,
        completedWorksOptions,
        completedWorks,
        files,
        setFiles,
        selectedOperatorId,
        selectedWorksiteId,
        selectedApartmentId,
        selectedBuildingId,
        computedStepperSteps,
        handleNextStep,
        handlePrevStep,
        handleChange,
        handleOperatorChange,
        handleWorksiteChange,
        handleApartmentChange,
        handleBuildingChange,
        handleCompletedWorksChange,
        quitus,
        canGoNext,
        canGoPrev,
        signatureCanvasRef,
        setIsEmpty,
        handleClearSignature
    } = useNewQuitusPageVM();

    return (
        <div className="new-quitus-page">
            <Container className="stepper-container">
                <Typography intrinsic="h2" className="mb-large-spacing">
                    {steps[currentStep - 1].label}
                </Typography>
                <MinimalStepper steps={computedStepperSteps} />
            </Container>
            <Container className="new-quitus-page-content">
                {currentStep === 1 && (
                    <Container bordered="grey" padding="medium small-medium" className="step-container">
                        <div className="step-content">
                            <Typography intrinsic="h4" defaultSpacing className="mb-none">
                                Informations générales
                            </Typography>
                            <Input
                                placeholder="Client"
                                value={quitus.client}
                                onValueChange={(value: string) => handleChange('client', value)}
                                type="email"
                            />
                            <Textarea
                                placeholder="Adresse"
                                value={quitus.address}
                                onValueChange={(value: string) => handleChange('address', value)}
                            />
                            <Input
                                placeholder="Téléphone du client"
                                value={quitus.phone_number}
                                type="number"
                                pattern="\d*"
                                onValueChange={(value: string) => handleChange('phone_number', value)}
                            />
                            <Input
                                placeholder="Portable du client"
                                value={quitus.cellphone_number}
                                type="number"
                                pattern="\d*"
                                onValueChange={(value: string) => handleChange('cellphone_number', value)}
                            />
                        </div>
                    </Container>
                )}
                {currentStep === 2 && (
                    <>
                        <Container bordered="grey" padding="medium small-medium" className="step-container">
                            <div className="step-content">
                                <Typography intrinsic="h4" defaultSpacing className="mb-none">
                                    Informations générales
                                </Typography>
                                <Input
                                    type="date"
                                    placeholder="Appel reçu le"
                                    value={quitus.call_date}
                                    onValueChange={(value: string) => handleChange('call_date', value)}
                                    max={new Date().toISOString().split('T')[0]}
                                />
                                <Dropdown
                                    options={operatorsOptions}
                                    placeholder="Responsable"
                                    value={operatorsOptions?.find((op) => op.id === selectedOperatorId) || undefined}
                                    onOptionChange={(value) => handleOperatorChange(value.id)}
                                />
                                <Dropdown
                                    options={worksitesOptions}
                                    placeholder="Chantier"
                                    value={
                                        selectedWorksiteId && worksitesOptions.length > 0
                                            ? worksitesOptions.find((worksite) => worksite.id === selectedWorksiteId) || null
                                            : null
                                    }
                                    onOptionChange={(value) => handleWorksiteChange(value.id)}
                                />
                                {buildingsOptions.length > 0 && (
                                    <Dropdown
                                        options={buildingsOptions}
                                        placeholder="Bâtiment"
                                        onOptionChange={(option) => handleBuildingChange(option.id)}
                                        value={
                                            selectedBuildingId && buildingsOptions.length > 0
                                                ? buildingsOptions.find((building) => building.id === selectedBuildingId) || null
                                                : null
                                        }
                                        disabled={!selectedWorksiteId}
                                        isSearchable={true}
                                    />
                                )}
                                <Dropdown
                                    options={apartmentsOptions}
                                    placeholder="Appartement"
                                    value={
                                        selectedApartmentId && apartmentsOptions.length > 0
                                            ? apartmentsOptions.find((apartment) => apartment.id === selectedApartmentId) || null
                                            : null
                                    }
                                    onOptionChange={(value) => handleApartmentChange(value.id)}
                                />
                                <Input
                                    placeholder="Autre n° d'appartement"
                                    value={quitus.apartment_other}
                                    onValueChange={(value: string) => handleChange('apartment_other', value)}
                                />
                            </div>
                        </Container>
                        <Container bordered="grey" padding="medium small-medium" className="step-container mt-medium-spacing">
                            <div className="step-content">
                                <Typography intrinsic="h4" defaultSpacing className="mb-none">
                                    Heure d'arrivée et de départ
                                </Typography>
                                <Input
                                    type="time"
                                    placeholder="Date d'arrivée"
                                    value={quitus.start_time}
                                    onValueChange={(value: string) => handleChange('start_time', value)}
                                />
                                <Input
                                    type="time"
                                    placeholder="Date de départ"
                                    value={quitus.end_time}
                                    onValueChange={(value: string) => handleChange('end_time', value)}
                                    min={quitus.start_time ?? ''}
                                />
                            </div>
                        </Container>
                    </>
                )}
                {currentStep === 3 && (
                    <>
                        <Container bordered="grey" padding="medium small-medium" className="step-container">
                            <div className="step-content">
                                <Typography intrinsic="h4" defaultSpacing className="mb-none">
                                    Intervention
                                </Typography>
                                <Dropdown
                                    options={quitusTypesOptions}
                                    placeholder="Mode d'intervention"
                                    value={quitusTypesOptions.find((type) => type.id === quitus.type.id) || null}
                                    onOptionChange={(value) => handleChange('type', value)}
                                />
                            </div>
                        </Container>
                        <Container bordered="grey" padding="medium small-medium" className="step-container mt-medium-spacing">
                            <div className="step-content">
                                <Typography intrinsic="h4" defaultSpacing className="mb-none">
                                    Nature de la demande
                                </Typography>
                                <Textarea
                                    placeholder="Nature de la demande"
                                    value={quitus.request_nature}
                                    onValueChange={(value: string) => handleChange('request_nature', value)}
                                />
                            </div>
                        </Container>
                        <Container bordered="grey" padding="medium small-medium" className="step-container mt-medium-spacing">
                            <div className="step-content">
                                <Typography intrinsic="h4" defaultSpacing className="mb-none">
                                    Travaux exécutés
                                </Typography>
                                <Typography intrinsic="p" defaultSpacing className="mb-none">
                                    Appuyez sur la touche "Entrée" pour ajouter une tâche
                                </Typography>
                                <InputDropdownMulti
                                    placeholder="Travaux effectués"
                                    onOptionChange={(options) => handleCompletedWorksChange(options.map((option) => option.id))}
                                    value={completedWorks.map(
                                        (completedWork) =>
                                            completedWorksOptions.find((option) => option.id === completedWork) ?? {
                                                id: completedWork,
                                                label: completedWork
                                            }
                                    )}
                                />
                            </div>
                        </Container>
                    </>
                )}
                {currentStep === 4 && <FileUploader files={files} setFiles={setFiles} overflow={false} />}
                {currentStep === 5 && (
                    <Container bordered="grey" padding="medium small-medium" className="step-container">
                        <div className="step-content">
                            <Typography intrinsic="h4" defaultSpacing className="mb-none">
                                Commentaire
                            </Typography>
                            <Textarea
                                placeholder="Commentaire"
                                value={quitus.observations}
                                onValueChange={(value: string) => handleChange('observations', value)}
                            />
                        </div>
                    </Container>
                )}
                {currentStep === 6 && (
                    <>
                        <Container bordered="grey" padding="medium small-medium" className="step-container">
                            <div className="step-content">
                                <Typography intrinsic="h4" defaultSpacing>
                                    Suite à donner
                                </Typography>
                            </div>

                            <Checkbox
                                label="Devis"
                                checked={!!quitus.follow_up.includes(1)}
                                onChange={(value: boolean) => {
                                    handleChange(
                                        'follow_up',
                                        value ? [...quitus.follow_up, 1] : quitus.follow_up.filter((item) => item !== 1)
                                    );
                                    return false;
                                }}
                                className="mb-small-spacing"
                            />
                            <Checkbox
                                label="Transmission quitus"
                                checked={!!quitus.follow_up.includes(2)}
                                onChange={(value: boolean) => {
                                    handleChange(
                                        'follow_up',
                                        value ? [...quitus.follow_up, 2] : quitus.follow_up.filter((item) => item !== 2)
                                    );
                                    return false;
                                }}
                                className="mb-small-spacing"
                            />
                            <Checkbox
                                label="Rappel"
                                checked={!!quitus.follow_up.includes(3)}
                                onChange={(value: boolean) => {
                                    handleChange(
                                        'follow_up',
                                        value ? [...quitus.follow_up, 3] : quitus.follow_up.filter((item) => item !== 3)
                                    );
                                    return false;
                                }}
                            />
                        </Container>
                        <Container bordered="grey" padding="medium small-medium" className="step-container mt-medium-spacing">
                            <div className="step-content">
                                <Typography intrinsic="h4" defaultSpacing>
                                    Signature du client
                                </Typography>
                                <SignatureCanvas
                                    throttle={24}
                                    velocityFilterWeight={1}
                                    penColor="black"
                                    canvasProps={{
                                        className: 'sigCanvas'
                                    }}
                                    onEnd={() => setIsEmpty(false)}
                                    ref={signatureCanvasRef}
                                />
                                <div className="buttons">
                                    <Button
                                        icon={<RiArrowGoBackLine />}
                                        iconSize={25}
                                        onClick={handleClearSignature}
                                        iconColor="green"
                                        variant="secondary"
                                        color="green"
                                        className="refresh-signature-button"
                                    >
                                        <></>
                                    </Button>
                                </div>
                            </div>
                        </Container>
                    </>
                )}
            </Container>

            <CustomBottomBar>
                <Button
                    color="marine"
                    variant="secondary"
                    onClick={handlePrevStep}
                    disabled={!canGoPrev}
                    className="sm-bottom-bar-button mr-small-spacing"
                >
                    <Icon className="arrow-left" color="grey" icon={<LiaArrowLeftSolid />} size={30} />
                </Button>
                <Button
                    variant="primary"
                    color="marine"
                    size="fullwidth"
                    onClick={handleNextStep}
                    disabled={!canGoNext}
                    className="next-button"
                    icon={<MdKeyboardArrowRight />}
                    iconPosition="forced-right"
                    iconColor="white"
                    iconSize={30}
                >
                    <Typography intrinsic="h4">Suivant</Typography>
                </Button>
            </CustomBottomBar>
        </div>
    );
};
